import { useTranslation } from 'react-i18next';
import { buildNextSteps, passengerListLabel } from './pnr-sheet-model';
import { buildCoupons, type CouponField, type CouponSource } from './pnr-coupon-model';
import type { PrintFormatSpec } from './print-formats';

// The voucher as a COUPON — what comes out of the printer that prints air
// tickets.
//
// An ATB unit is loaded with 8 × 3¼ in stock and prints one coupon at a time.
// That is not a small A4 page: the document sheet is a thing you read once,
// front to back, holding it; a coupon is a thing that gets SEPARATED. The hotel
// coupon ends up on a front desk, the transport coupon in a driver's hand, the
// flight coupon at a counter — so each one repeats the header that identifies
// it (airline, PNR, passenger, issued-at) and carries its own instruction at the
// bottom, because whoever is holding it will not be holding the others.
//
// This is also why the pack does not simply reprint its five sections onto five
// coupons. Ticket stock is a consumable an agent has to reload by hand at 2 a.m.
// — the directions ride along on the hotel coupon (they are only ever read
// together with it) and a section with nothing to say does not claim a coupon at
// all. What is left is at most four: flight, hotel, card, ride.
//
// The 80 mm roll is the same content on a different geometry: no page breaks
// (vertical paper is free), one narrow column, and — since the roll can afford
// it — the numbered "what happens next" that the ATB coupons have no room for.
//
// PII: names print MASKED here exactly as they do on the document sheet — the
// coupon binds by PNR + reference, which is what a counter verifies against an
// ID document.

/**
 * The coupons, drawn at the physical size of the stock they are going to.
 *
 * Dimensions come off the format in millimetres and are used unchanged on
 * screen, so the preview in the dialog is the coupon at 1:1 — an operator can
 * see that a hotel name is going to overflow before they spend the stock, which
 * a scaled-down thumbnail would hide.
 *
 * Ticket stock breaks after every coupon (one per piece) and clips its overflow,
 * so a long value can never bleed into the next coupon's first line. The roll
 * does neither: it is one continuous column with rules between the sections and
 * a cut mark at the end.
 */
export function PnrCouponSheet({
  source,
  format,
  exerciseLabel,
}: {
  source: CouponSource;
  format: PrintFormatSpec;
  /**
   * #812 exercise mode: the rehearsal label. Every coupon gets a one-line band under its
   * header (each piece is read alone, so each piece says it) plus a faint diagonal
   * watermark. The band lives INSIDE the fixed-height, overflow-clipped ticket article
   * and costs one 7pt line, so the 82.55 mm geometry and the page breaks are untouched.
   * Absent = the production coupon.
   */
  exerciseLabel?: string | undefined;
}) {
  const { t } = useTranslation('pnrActions');
  const coupons = buildCoupons(source, t);
  const isTicket = format.stock === 'ticket';
  const { group, tenant } = source;
  const exercise = exerciseLabel?.trim() || undefined;
  const issued = new Date().toLocaleString();
  const steps =
    !isTicket && source.kind === 'travel-pack'
      ? buildNextSteps({
          group,
          reservation: source.reservation,
          card: source.card,
          hasCareMoney: !!source.card || typeof group.mealVoucher?.amountUsd === 'number',
          trips: source.trips,
        }, t)
      : [];

  if (!coupons.length) {
    return (
      <div
        className="mx-auto bg-white font-mono text-black"
        style={{ width: `${format.widthMm}mm` }}
        data-print-sheet
        data-print-format={format.id}
      >
        <div className="p-[5mm] text-[9pt]" data-testid="coupon-empty">
          {t('coupon.empty')}
        </div>
      </div>
    );
  }

  return (
    <div
      className={isTicket ? 'mx-auto flex flex-col items-center gap-4 print:gap-0' : 'mx-auto'}
      style={isTicket ? undefined : { width: `${format.widthMm}mm` }}
      data-print-sheet
      data-print-format={format.id}
    >
      {steps.length ? (
        <section
          className="border-b border-dashed border-black px-[4mm] py-[3mm] font-mono text-[8pt] leading-[1.35] text-black"
          data-testid="coupon-next-steps"
        >
          <p className="text-[7pt] font-bold uppercase tracking-[0.18em]">
            {t('sheet.nextSteps')}
          </p>
          <ol className="mt-[1.5mm] list-decimal space-y-[1mm] pl-[4mm]">
            {steps.map((s) => (
              <li key={s}>{s}</li>
            ))}
          </ol>
        </section>
      ) : null}

      {coupons.map((coupon, i) => (
        <article
          key={coupon.id}
          data-testid={`coupon-${coupon.id}`}
          data-coupon=""
          className={
            isTicket
              ? `relative flex flex-col overflow-hidden border border-neutral-300 bg-white p-[5mm] font-mono text-[9pt] leading-[1.25] text-black print:border-0 ${
                  i < coupons.length - 1 ? 'print:break-after-page' : ''
                }`
              : 'relative flex flex-col border-b border-dashed border-black bg-white px-[4mm] py-[3mm] font-mono text-[8pt] leading-[1.3] text-black last:border-b-0'
          }
          style={
            isTicket
              ? { width: `${format.widthMm}mm`, height: `${format.heightMm}mm` }
              : { width: `${format.widthMm}mm` }
          }
        >
          {/* #812: faint diagonal watermark behind the content. Absolutely positioned,
              so it takes no flow space; `pointer-events-none` so nothing underneath
              changes behaviour; still printed because it is text, not a background. */}
          {exercise ? (
            <span
              aria-hidden
              data-testid="coupon-exercise-watermark"
              className="pointer-events-none absolute inset-0 flex select-none items-center justify-center overflow-hidden text-center font-bold uppercase leading-none tracking-[0.2em] text-black opacity-[0.08] print:opacity-[0.12]"
              style={{ fontSize: isTicket ? '22pt' : '16pt', transform: 'rotate(-14deg)' }}
            >
              {exercise}
            </span>
          ) : null}
          <header className="flex items-baseline justify-between border-b border-black pb-[1.5mm]">
            <span className="text-[7pt] font-bold uppercase tracking-[0.18em]">
              {tenant} · {coupon.title}
            </span>
            <span className="text-[7pt] uppercase tracking-[0.12em]">
              {i + 1}/{coupons.length}
            </span>
          </header>
          {/* #812: the band. One clipped line right under the header, on EVERY coupon —
              whoever holds this one piece must read it without the others. */}
          {exercise ? (
            <p
              role="note"
              data-testid="coupon-exercise-band"
              className="mt-[1mm] truncate border border-black bg-neutral-200 px-[1.5mm] py-[0.5mm] text-center text-[7pt] font-bold uppercase tracking-[0.18em]"
            >
              {exercise}
            </p>
          ) : null}

          {/* Side by side on a 203 mm coupon; stacked on 80 mm, where putting the
              locator beside a wrapping hotel name leaves neither of them room. */}
          <div
            className={`mt-[2mm] flex gap-[3mm] ${
              isTicket ? 'items-baseline justify-between' : 'flex-col'
            }`}
          >
            {/* Ticket stock is a fixed 82.55 mm and clips, so a long hotel name
                is truncated rather than allowed to push the footer off the
                coupon. A roll has no page: nothing there is ever cut, because
                vertical paper is the one thing that surface has to spare. */}
            <div className="min-w-0">
              <p
                className={`text-[14pt] font-bold uppercase leading-none ${
                  isTicket ? 'truncate' : 'break-words'
                }`}
              >
                {coupon.headline}
              </p>
              {coupon.sub ? (
                <p className={`mt-[1mm] text-[9pt] ${isTicket ? 'truncate' : ''}`}>{coupon.sub}</p>
              ) : null}
            </div>
            <div className={isTicket ? 'shrink-0 text-right' : 'shrink-0'}>
              <p className="text-[7pt] uppercase tracking-[0.12em]">{t('sheet.pnr')}</p>
              <p className="text-[13pt] font-bold leading-none tracking-[0.06em]">
                {group.pnrLabel}
              </p>
            </div>
          </div>

          {/* The party gets a line of its own rather than a cell in the grid: a
              front desk checks documents against THIS line, so it must not be
              the first thing a four-column layout truncates. */}
          <p
            className={`mt-[1.5mm] border-b border-neutral-300 pb-[1.5mm] text-[8.5pt] font-bold uppercase ${
              isTicket ? 'truncate' : ''
            }`}
            data-testid="coupon-party"
          >
            <span className="mr-[1.5mm] text-[6.5pt] font-normal tracking-[0.1em] text-neutral-600">
              {t('coupon.party', { count: group.paxCount })}
            </span>
            {passengerListLabel(source.passengerNames, isTicket ? 4 : 8, t)}
          </p>

          <div
            className={`mt-[2mm] grid gap-x-[3mm] gap-y-[1.5mm] ${
              isTicket ? 'grid-cols-4' : 'grid-cols-2'
            }`}
          >
            {coupon.fields.map((f) => (
              <CouponCell key={f.label} label={f.label} value={f.value} clip={isTicket} />
            ))}
          </div>

          {coupon.note ? (
            <p className="pt-[2mm] text-[7pt] leading-[1.3]">{coupon.note}</p>
          ) : null}
          {coupon.link ? (
            <p className="break-all pt-[1mm] text-[6.5pt] leading-[1.2]">{coupon.link}</p>
          ) : null}

          {/* `mt-auto` here, not on the note: on fixed-height stock the issued-at
              line is what sits on the tear edge, and the instruction reads
              directly under the fields it belongs to. */}
          <footer
            className={`mt-auto border-t border-neutral-400 pt-[1mm] text-[6.5pt] uppercase tracking-[0.1em] ${
              isTicket ? 'flex items-baseline justify-between' : 'flex flex-col gap-[0.5mm]'
            }`}
          >
            <span>{t('coupon.issued', { issued })}</span>
            <span>{t('coupon.validWithId')}</span>
          </footer>
        </article>
      ))}

      {!isTicket ? (
        <p
          className="py-[3mm] text-center font-mono text-[7pt] uppercase tracking-[0.2em] text-black"
          data-testid="coupon-cut"
        >
          ✂ — — — — — — — —
        </p>
      ) : null}
    </div>
  );
}

function CouponCell({ label, value, clip }: CouponField & { clip: boolean }) {
  return (
    <p className="min-w-0">
      <span className="block text-[6.5pt] uppercase tracking-[0.1em] text-neutral-600">{label}</span>
      <span className={`block font-bold ${clip ? 'truncate' : 'break-words'}`}>{value}</span>
    </p>
  );
}
